import { Button } from '@bambi/component-library/src/components/Button';
import { EnvelopeIcon, PhoneIcon } from '@heroicons/react/24/outline';
import upperFirst from 'lodash-es/upperFirst';

import { MembershipRead } from 'src/common/external/bambi-api/bambiApi';
import { formatPhoneNumber } from 'src/common/formatPhoneNumber';
import { Avatar } from 'src/common/primitives/Avatar';
import { Modal, ModalProps } from 'src/common/primitives/Modal';
import { ModalTitle } from 'src/common/primitives/Modal/ModalTitle';
import { Well } from 'src/common/primitives/Well/Well';
import { formatFullName } from 'src/common/util/formatFullName';

interface MemberDuplicateNameModalProps
  extends Omit<ModalProps, 'title' | 'children'> {
  membership: MembershipRead | null;
  onCreate: () => void;
  onEdit: (membership: MembershipRead) => void;
  onRestore: (membership: MembershipRead) => void;
}

export function MemberDuplicateNameModal({
  open,
  membership,
  setOpen,
  onClose,
  onCreate,
  onEdit,
  onRestore,
}: MemberDuplicateNameModalProps) {
  if (!membership) {
    return null;
  }

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      onClose={onClose}
      title="Duplicate member name detected"
      contentClassnames="max-w-lg"
    >
      <div className="flex flex-col gap-2">
        <ModalTitle>Existing membership found</ModalTitle>
        <p className="text-sm text-gray-500">
          A member with this first and last name already exists.
        </p>
        <Well skipPrintable>
          <div className="flex flex-row gap-2">
            <div>
              <Avatar
                firstName={membership.first_name}
                lastName={membership.last_name}
              />
            </div>
            <div className="border-t border-gray-500"></div>
            <div className="flex w-full flex-col">
              <strong>{formatFullName(membership)}</strong>
              <span className="text-sm text-gray-500">
                {upperFirst(membership.role)}
              </span>
            </div>
          </div>
          <div className="flex w-full flex-col gap-2">
            <div className="border-t border-gray-300"></div>
            <div className="flex flex-row gap-2 text-sm text-gray-500">
              <EnvelopeIcon className="h-5 w-5" />
              {membership.email}
            </div>
            <div className="flex flex-row gap-2 text-sm text-gray-500">
              <PhoneIcon className="h-5 w-5" />
              {formatPhoneNumber(membership.phone_number)}
            </div>
          </div>
        </Well>
        <div className="mt-2 flex flex-row gap-2">
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="secondary"
            className="whitespace-nowrap"
            onClick={onCreate}
          >
            Create anyway
          </Button>
          {membership.deleted_at ? (
            <Button
              variant="primary"
              className="w-full"
              onClick={() => onRestore(membership)}
            >
              Restore member
            </Button>
          ) : (
            <Button
              variant="primary"
              className="w-full"
              onClick={() => onEdit(membership)}
            >
              Update email
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}
